import React from 'react';
import { useLocation, useNavigationType, NavigationType } from 'react-router-dom';

const FORM_CONTROLS = new Set(['BUTTON', 'INPUT', 'SELECT', 'TEXTAREA']);
// <a>/<area> only take focus when they actually link somewhere.
const LINKS = new Set(['A', 'AREA']);

/**
 * Whether the element takes focus as it stands. Anything that doesn't — a
 * plain <section> or <div> anchor target, which is what most of ours are —
 * needs a temporary tabindex="-1" before it can be focused programmatically.
 */
const isAlreadyFocusable = (el: HTMLElement): boolean => {
  if (el.hasAttribute('tabindex') || el.isContentEditable) {
    return true;
  }
  if (LINKS.has(el.tagName)) {
    return el.hasAttribute('href');
  }
  return FORM_CONTROLS.has(el.tagName) && !el.hasAttribute('disabled');
};

/**
 * Move focus to a hash target so keyboard users land where the viewport does.
 *
 * Without this, scrolling alone leaves focus at the document start: the next
 * Tab press throws the user back to the top of the page, undoing the jump.
 * (WCAG 2.4.3 Focus Order.) Targets that aren't focusable get tabindex="-1"
 * for the duration of the visit — removed on blur so the DOM is left as we
 * found it and the element stays out of the tab order.
 *
 * @param {HTMLElement} el - The hash target to focus
 * @returns {() => void} A teardown that drops the temporary tabindex the blur listener
 *   never got to. A target still holding focus when the user navigates away
 *   never blurs, so without this it would keep the attribute for good — and
 *   "left as we found it" would only be true for targets the user tabbed off.
 */
const focusTarget = (el: HTMLElement): (() => void) => {
  if (isAlreadyFocusable(el)) {
    el.focus({ preventScroll: true });
    return () => {};
  }
  const clearTabIndex = () => el.removeAttribute('tabindex');
  el.setAttribute('tabindex', '-1');
  el.addEventListener('blur', clearTabIndex, { once: true });
  el.focus({ preventScroll: true });
  return () => {
    el.removeEventListener('blur', clearTabIndex);
    clearTabIndex();
  };
};

const ScrollToTop: React.FC = () => {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType();
  React.useEffect(() => {
    // On browser back/forward (POP), let the native scroll-restoration
    // restore the prior position — overriding it with scrollTo(0, 0) loses
    // the user's place when they navigate back. (#604)
    if (navigationType === NavigationType.Pop) {
      return;
    }
    // No hash → scroll to top, as before.
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }
    // Pages here are lazy-loaded and may load content above the target
    // (TOCs, images) after the initial scroll, which pushes the anchor
    // back down. Poll for the element, then re-scroll until its top
    // stabilizes near 0 — or until we exhaust the window.
    const targetSelector = `#${CSS.escape(hash.slice(1))}`;
    let attempts = 0;
    let focused = false;
    let releaseTarget: (() => void) | undefined;
    let frame = 0;
    const MAX_ATTEMPTS = 120; // ~2s at 60fps
    const TOLERANCE_PX = 4;
    const tryScroll = () => {
      const el = document.querySelector<HTMLElement>(targetSelector);
      attempts += 1;
      if (el) {
        const offset = el.getBoundingClientRect().top;
        const landed = Math.abs(offset) <= TOLERANCE_PX;
        if (!landed) {
          el.scrollIntoView({ block: 'start' });
        }
        // Focus once, the first frame the target exists. Re-focusing on
        // later frames would fight a user who tabbed away while the rest
        // of the page was still settling.
        if (!focused) {
          focused = true;
          releaseTarget = focusTarget(el);
        }
        if (landed) {
          return;
        }
      }
      if (attempts < MAX_ATTEMPTS) {
        frame = requestAnimationFrame(tryScroll);
      } else if (!el) {
        window.scrollTo(0, 0);
      }
    };
    frame = requestAnimationFrame(tryScroll);
    // Navigating away mid-poll has to stop the loop: a stray scroll on the
    // page the user moved on to is bad enough, and now the loop also moves
    // focus, which would yank them out of wherever they've since landed.
    // Releasing the target here also covers the case where it still holds
    // focus, so its temporary tabindex never outlives the visit.
    return () => {
      cancelAnimationFrame(frame);
      releaseTarget?.();
    };
  }, [pathname, hash, navigationType]);
  return null;
};

export default ScrollToTop;
